import {CONTENT_API_URL} from "../../config"
import {LANGS} from "../../i18n"

// Slug constant de la page ContentHub (défini côté API dans services/travel.js).
const CONTENT_HUB_SLUG = "jewish-educations"

/**
 * Construit l'URL d'une catégorie ContentHub en FORÇANT un site/langue donné.
 *
 * Sert au menu dynamique : quand les catégories d'une langue sont absentes, on
 * affiche le fallback EN — et les liens doivent alors pointer vers la version EN
 * de la page (`/en/jewish-educations/category/{aid}`), sinon la page catégorie
 * résout `langValue` depuis l'URL/org et l'aid EN ne matche aucun article.
 *
 * `org.BASE_WEB_URL` contient déjà le préfixe de langue COURANT (ajouté en SSR).
 * On retire ce dernier segment s'il s'agit d'une langue connue, puis on réinsère
 * `targetSite` — sauf si c'est la langue par défaut de l'org (pas de préfixe).
 *
 * @param {object} org - org du contexte (BASE_WEB_URL, language)
 * @param {string} targetSite - code langue cible (ex: "en" en fallback)
 * @param {number|string} aid - identifiant de catégorie
 */
// Base de l'URL ContentHub avec le préfixe de langue forcé à `targetSite`.
// `org.BASE_WEB_URL` contient déjà le préfixe COURANT : on le retire s'il s'agit
// d'une langue connue, puis on réinsère `targetSite` (sauf langue par défaut).
// Retourne les segments jusqu'au slug ContentHub (sans /category/...).
function contentHubBaseParts(org, targetSite) {
    const baseWebUrl = (org?.BASE_WEB_URL || "").replace(/\/+$/, "")
    const parts = baseWebUrl.split("/")

    // Retire le(s) segment(s) de langue déjà présents en queue de BASE_WEB_URL
    // (préfixe de langue courant ajouté en amont par RouterService) avant de
    // réinjecter `targetSite`. Boucle plutôt qu'un seul pop = défense robuste si
    // BASE_WEB_URL arrivait déjà avec un préfixe doublé.
    while (parts.length > 0 && LANGS.find(l => l.value === parts[parts.length - 1])) {
        parts.pop()
    }

    const defaultLang = org?.language || "fr"
    if (targetSite && targetSite !== defaultLang) {
        parts.push(targetSite)
    }

    parts.push(CONTENT_HUB_SLUG)
    return parts
}

/**
 * URL de la home ContentHub (`/jewish-educations`) avec préfixe de langue forcé.
 * Sert de cible à l'item parent du menu dynamique.
 */
export function buildContentHubHomeUrlForSite(org, targetSite) {
    return contentHubBaseParts(org, targetSite).join("/")
}

/**
 * Construit l'URL d'une catégorie ContentHub en FORÇANT un site/langue donné.
 *
 * Sert au menu dynamique : quand les catégories d'une langue sont absentes, on
 * affiche le fallback EN — et les liens doivent alors pointer vers la version EN
 * de la page (`/en/jewish-educations/category/{aid}`), sinon la page catégorie
 * résout `langValue` depuis l'URL/org et l'aid EN ne matche aucun article.
 *
 * @param {object} org - org du contexte (BASE_WEB_URL, language)
 * @param {string} targetSite - code langue cible (ex: "en" en fallback)
 * @param {number|string} aid - identifiant de catégorie
 */
export function buildCategoryUrlForSite(org, targetSite, aid) {
    return [...contentHubBaseParts(org, targetSite), "category", aid].join("/")
}

/**
 * Aplati l'arbre de catégories ContentHub en items de menu virtuels reliés par
 * `parent`, prêts à être injectés dans le `fullMenu` d'un Header récursif.
 *
 * Chaque catégorie (à tout niveau) devient un item cliquable vers ses articles,
 * et ses `children` deviennent des items enfants → la cascade du Header les rend
 * naturellement en sous-menus imbriqués (toute la profondeur de l'arbre).
 *
 * @param {Array} categories - arbre (contentHubCategories.items)
 * @param {string} parentId - id du menu parent (l'item DYNAMIC_ARTICLES)
 * @param {string} site - site réellement servi (pour forcer le préfixe de langue)
 * @param {object} org
 * @returns {Array} items plats {id, parent, name, url}
 */
export function buildDynamicArticlesMenuItems(categories, parentId, site, org) {
    if (!Array.isArray(categories) || categories.length === 0) return []
    const out = []
    const walk = (nodes, parent) => {
        for (const cat of nodes) {
            if (!cat || cat.aid == null) continue
            const id = `dyn-${parentId}-${cat.aid}`
            out.push({
                id,
                parent,
                name: cat.title,
                url: buildCategoryUrlForSite(org, site, cat.aid),
            })
            if (Array.isArray(cat.children) && cat.children.length > 0) {
                walk(cat.children, id)
            }
        }
    }
    walk(categories, parentId)
    return out
}

export function buildBaseUrl(context) {
    const baseWebUrl = context?.org?.BASE_WEB_URL || ""
    const pageRoute = Array.isArray(context?.page?.route) ? context.page.route : []
    const prefix = pageRoute.join("/")
    return [baseWebUrl.replace(/\/+$/, ""), prefix].filter(Boolean).join("/")
}

export function buildHomeUrl(context) {
    return buildBaseUrl(context) || "/"
}

export function buildCategoryUrl(context, categoryAid) {
    return `${buildBaseUrl(context)}/category/${categoryAid}`
}

export function buildArticleUrl(context, aid, slug) {
    const base = buildBaseUrl(context)
    if (slug) return `${base}/article/${aid}-${encodeURIComponent(slug)}`
    return `${base}/article/${aid}`
}

export function slugify(input) {
    if (!input) return ""
    return String(input)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60)
}

export function getDir(langValue) {
    return langValue === "he" ? "rtl" : "ltr"
}

export function imageUrl(urlLocal) {
    if (!urlLocal) return null
    if (/^https?:/i.test(urlLocal)) return urlLocal
    return CONTENT_API_URL.replace(/\/+$/, "") + urlLocal
}

export function pickThumbnail(article) {
    if (!article) return null
    if (article.image_url) return imageUrl(article.image_url)
    if (Array.isArray(article.images) && article.images.length) {
        const thumb = article.images.find(i => i.role === "thumbnail" || i.role === "og:image" || i.role === "schema:image")
        return imageUrl((thumb || article.images[0]).url_local)
    }
    return null
}

export function pickCategoryImage(category) {
    if (!category) return null
    return imageUrl(category.image_url)
}

/**
 * Re-ecrit les <img src="/v1/images/..."> dans le HTML rendu cote API
 * pour les prefixer avec CONTENT_API_URL et les rendre lazy.
 */
export function rewriteArticleHtml(html) {
    if (!html) return ""
    const base = CONTENT_API_URL.replace(/\/+$/, "")
    return html
        .replace(/<img\s+([^>]*?)src=(["'])(\/v1\/images\/[^"']+)\2/gi,
            `<img $1src=$2${base}$3$2 loading="lazy"`)
        .replace(/<a\s+([^>]*?)href=(["'])(\/[^"']+)\2/gi,
            `<a $1href=$2${base}$3$2 target="_blank" rel="noopener noreferrer"`)
}
